import React from 'react'
import { IconInfo } from '../common/Icons'
import TooltipClick from '../common/TooltipClick'
import { isEmpty } from '../../utils'
import clsx from 'clsx'

interface Props {
  airbnbCancellationPolicies: any
  isContentBookingSummary?: boolean
}

const HotelAirbnbCancelPolicy: React.FC<Props> = ({ airbnbCancellationPolicies, isContentBookingSummary }) => {
  const isLongTerm = !isEmpty(airbnbCancellationPolicies?.ltsCancellationPolicyId)
  const isStandardPolicy = !isEmpty(airbnbCancellationPolicies?.cancellationPolicyCategory)
  const isNonRefundable = !isEmpty(airbnbCancellationPolicies?.nonRefundablePriceFactor)
  const renderTitle = (isStandard: boolean) => {
    return (
      <div className="dtravel-center-horizontal gap-2">
        <span className="dtravel-text-12-16 dtravel-inter-600 tracking-[0.48px] uppercase dtravel-grayscale-600">
          {isStandard ? 'standard' : 'long-term'}
        </span>
        <TooltipClick
          placement={'bottom'}
          type={isStandard ? 'airbnb_standard_cancel_policy' : 'airbnb_long_term_cancel_policy'}
          title={isStandard ? 'Applies for stays of under 28 nights' : 'Applies for stays of 28+ nights'}
          sx={{ fontSize: 12, lineHeight: '16px' }}
          arrow
        >
          <IconInfo />
        </TooltipClick>
      </div>
    )
  }
  const renderItem = (name: string, value: string) => {
    return (
      <div
        className={clsx(
          'flex dtravel-flex-col items-center dtravel-gap-24 p-[24px] dtravel-rounded-16 border border-grayscale-300',
          isContentBookingSummary ? 'lg:flex-row' : 'md:flex-row'
        )}
      >
        <p
          className={clsx(
            'dtravel-w-full xl:w-[200px] xl:min-w-[200px]',
            isContentBookingSummary ? 'lg:w-[136px] lg:min-w-[136px]' : 'md:w-[136px] md:min-w-[136px]',
            'dtravel-text-12-16 dtravel-inter-600 tracking-[0.48px] dtravel-grayscale-600 uppercase'
          )}
        >
          {name}
        </p>
        <p className="dtravel-text-16-20 dtravel-inter-400 text-black" dangerouslySetInnerHTML={{ __html: value }} />
      </div>
    )
  }
  const renderStandardRefundableDescription = () => {
    const { cancellationPolicyCategory } = airbnbCancellationPolicies
    if (cancellationPolicyCategory === 'flexible')
      return `100% refund up to 24 hours prior to arrival.<br/>After that, 100% refund for every unspent night but the first one.`
    if (cancellationPolicyCategory === 'moderate')
      return `100% refund up to 5 days prior to arrival.<br/>After that, 50% refund for every unspent night but the first one.`
    if (cancellationPolicyCategory === 'better_strict_with_grace_period') {
      return !isNonRefundable
        ? `100% refund up to 30 days prior to arrival. 
          <br/>
          100% refund for booking of less than 30 days prior to arrival, with the cancellation made within 48 hours of booking and at least 14 days before check-in.
          <br/>
          50% refund up to 7 days prior to arrival. 
          <br/>
          After that, no refund.`
        : `100% refund up to 30 days prior to arrival. 
          <br/>
          100% refund for booking of less than 30 days, with cancellation made within 48 hours of booking and at least 14 days before prior to arrival.
          <br/>
          50% refund up to 7 days prior to arrival. 
          <br/>
          After that, no refund.`
    }
    if (cancellationPolicyCategory === 'strict_14_with_grace_period') {
      return `100% refund for cancellation made within 48 hours of booking and at least 14 days prior to arrival.
          <br/>
          50% refund up to 7 days prior to arrival.
          <br/>
          After that, no refund.`
    }
    if (cancellationPolicyCategory === 'super_strict_30') {
      return `50% refund up to 30 days prior to arrival.<br/>After that, no refund.`
    }
    if (cancellationPolicyCategory === 'super_strict_60') {
      return `50% refund up to 60 days prior to arrival.<br/>After that, no refund.`
    }
    return ''
  }
  const renderLongTermDescription = () => {
    const { ltsCancellationPolicyId } = airbnbCancellationPolicies
    if (ltsCancellationPolicyId === 'CANCEL_LONG_TERM_WITH_GRACE_PERIOD')
      return `100% refund for cancellation made within 48 hours of booking and at least 28 days before prior to arrival.
        <br/>
        After that, no refund for the first 30 days of the stay.`
    if (ltsCancellationPolicyId === 'CANCEL_LONG_TERM_FAIR')
      return `100% refund up to 30 days prior to arrival.
        <br/>
        After that, no refund for the first 30 days of the stay.`
    return ''
  }
  return (
    <div className="flex dtravel-flex-col dtravel-gap-24">
      {isStandardPolicy && (
        <div className="flex dtravel-flex-col dtravel-gap-12">
          {renderTitle(true)}
          {renderItem('refundable', renderStandardRefundableDescription())}
          {isNonRefundable &&
            renderItem('Non-refundable', `Get a 10% discount but your reservation is non-refundable.`)}
        </div>
      )}
      {isLongTerm && (
        <div className="flex dtravel-flex-col dtravel-gap-12">
          {renderTitle(false)}
          {renderItem('refundable', renderLongTermDescription())}
        </div>
      )}
    </div>
  )
}

export default HotelAirbnbCancelPolicy
